import mixpanel, { type Config, type Mixpanel } from "mixpanel-browser";

export type AnalyticsRole = { key: string; name: string };
export type AnalyticsPage = {
  routeId: string;
  projectKey?: string;
  conceptKey?: string;
  creativeKey?: string;
  specificationKey?: string;
  revisionSequenceId?: string;
  generativeEditKey?: string;
};
export type AnalyticsContext = {
  principalKey?: string;
  /** Local transition boundary; never sent as attribution. */
  tenantKey?: string;
  emailDomain?: string;
  environment?: string;
  rolePending?: boolean;
  role?: AnalyticsRole;
  /** Local deduplication only; never sent to the SDK. */
  navigationId: string;
  page: AnalyticsPage;
};
type EventProperties = {
  "Page Viewed": AnalyticsPage;
  "Deliverable Download Clicked": { deliverableIntegrationKey: string };
  "Project ZIP Download Clicked": { projectIntegrationKey: string };
};
type EventName = keyof EventProperties;
type AnalyticsConfiguration = { token?: string; region?: string };

const DESTINATIONS: Record<string, string> = {
  US: "https://api-js.mixpanel.com",
  EU: "https://api-eu.mixpanel.com",
  IN: "https://api-in.mixpanel.com",
};
const EVENTS = new Set<EventName>([
  "Page Viewed",
  "Deliverable Download Clicked",
  "Project ZIP Download Clicked",
]);
const PROPERTIES = new Set([
  "token",
  "distinct_id",
  "$user_id",
  "$device_id",
  "$insert_id",
  "time",
  "routeId",
  "projectKey",
  "conceptKey",
  "creativeKey",
  "specificationKey",
  "revisionSequenceId",
  "generativeEditKey",
  "deliverableIntegrationKey",
  "projectIntegrationKey",
  "role",
  "roleKey",
  "is_authenticated",
  "email_domain",
  "environment",
]);

/** SDK-added URLs, referrers, campaigns and arbitrary superproperties never leave this boundary. */
const beforeSend: NonNullable<Config["hooks"]["before_send_events"]> = (event) => {
  if (!EVENTS.has(event.event as EventName)) return null;
  return {
    ...event,
    properties: Object.fromEntries(
      Object.entries(event.properties).filter(
        ([key, value]) => PROPERTIES.has(key) && value != null,
      ),
    ),
  };
};

export function createProductAnalytics() {
  let configuration: AnalyticsConfiguration = {};
  let sdk: Mixpanel | undefined;
  let context: AnalyticsContext | undefined;
  let identifiedPrincipal: string | undefined;
  let failed = false;
  let lastPage: string | undefined;

  const safely = (action: () => void) => {
    try {
      action();
    } catch {
      /* Telemetry must never change product behavior. */
    }
  };

  const send = <E extends EventName>(name: E, properties: EventProperties[E]) => {
    if (!sdk || failed || !context?.principalKey || context.principalKey !== identifiedPrincipal)
      return;
    const role = context.role;
    safely(() =>
      sdk?.track(name, {
        ...properties,
        is_authenticated: true,
        ...(context?.emailDomain ? { email_domain: context.emailDomain } : {}),
        ...(context?.environment ? { environment: context.environment } : {}),
        ...(role ? { role: role.name, roleKey: role.key } : {}),
      }),
    );
  };

  const reset = () => {
    context = undefined;
    identifiedPrincipal = undefined;
    try {
      sdk?.reset();
    } catch {
      failed = true;
    }
  };

  const identify = (principalKey: string) => {
    const token = configuration.token?.trim();
    const destination = configuration.region && DESTINATIONS[configuration.region];
    if (!token || !destination || failed) return;
    try {
      if (!sdk) {
        const config = {
          api_host: destination,
          autocapture: false,
          track_pageview: false,
          flags: false,
          remote_settings_mode: "disabled",
          record_sessions_percent: 0,
          record_heatmap_data: false,
          record_canvas: false,
          record_console: false,
          record_network: false,
          recording_event_triggers: {},
          track_marketing: false,
          save_referrer: false,
          store_google: false,
          skip_first_touch_marketing: true,
          stop_utm_persistence: true,
          ip: false,
          // No stored anonymous identity, persisted properties/queues, or cross-tab coordination.
          disable_persistence: true,
          batch_requests: false,
          hooks: { before_send_events: beforeSend },
          // 2.84 implements track_marketing but omits it from its bundled Config type.
        } satisfies Partial<Config> & { track_marketing: boolean };
        sdk = mixpanel.init(token, config, "cloud-product");
        // There is no anonymous history to merge. identify still sets distinct_id/$user_id.
        sdk.disable(["$identify", "$create_alias"]);
      }
      sdk.identify(principalKey);
      if (sdk.get_distinct_id() !== principalKey) {
        failed = true;
        return;
      }
      identifiedPrincipal = principalKey;
    } catch {
      failed = true;
    }
  };

  return {
    configure(next: AnalyticsConfiguration) {
      reset();
      lastPage = undefined;
      configuration = next;
      sdk = undefined;
      failed = false;
    },
    reset,
    getPrincipalKey: () => identifiedPrincipal,
    update(next: AnalyticsContext) {
      const principalChanged = context?.principalKey !== next.principalKey;
      const tenantChanged = context?.tenantKey !== next.tenantKey;
      if (principalChanged || tenantChanged) {
        reset();
      }
      // A completed visit must not disappear when navigation outruns enrichment.
      if (
        context?.rolePending &&
        context.principalKey &&
        context.navigationId !== next.navigationId
      ) {
        const previousPage = JSON.stringify([
          context.principalKey,
          context.tenantKey,
          context.navigationId,
        ]);
        if (lastPage !== previousPage) {
          lastPage = previousPage;
          send("Page Viewed", context.page);
        }
      }
      context = next;
      if (!next.principalKey) return;
      if (identifiedPrincipal !== next.principalKey) identify(next.principalKey);
      if (!identifiedPrincipal) return;
      if (next.rolePending) return;
      const pageIdentity = JSON.stringify([next.principalKey, next.tenantKey, next.navigationId]);
      if (lastPage !== pageIdentity) {
        lastPage = pageIdentity;
        send("Page Viewed", next.page);
      }
    },
    track<E extends EventName>(name: E, properties: EventProperties[E]) {
      send(name, properties);
    },
  };
}

export const productAnalytics = createProductAnalytics();
